import { tk } from '../msg/tokens';
import { getAgentHold, holdAgent, resumeAgent } from '../team-hold/teamHold';
import type { MsgField, MsgToken } from '../msg/tokens';
import type { PaymentLinkRow as ArPaymentLinkRow } from '../collections/types';
import type {
  ArAudiencePatient,
  ArBreakdown,
  ArByFollowUpRow,
  ArFieldKey,
  ArFollowUp,
  ArFollowUpDraft,
  ArFunnelRow,
  ArInsurerPatient,
  ArLatestItem,
  ArNumbers,
  ArPaidAfterRow,
  ArReadbackItem,
  ArSideTile,
  ArStartingPoint,
  ArStep,
  ArTodayStat,
} from './types';

/**
 * Accounts receivable & Collections demo — Alamo Heights Dental (amendment 28).
 *
 * One mock, and it adds up. The funnel subtracts 212·$47,900 down to 103·$20,310;
 * the four active follow-ups' "in it now" sum to 82 and their last-30 paid sum to
 * 74 · $11,240; the agent page and the Collections tabs all read these objects.
 */

/* ---- §1 the numbers ------------------------------------------------------ */

export const AR_NUMBERS: ArNumbers = {
  outstandingPatients: 212,
  outstandingValue: '$47,900',
  followablePatients: 103,
  followableValue: '$20,310',
  inFollowUp: 82,
  waiting: 21,
  collected30: '$11,240',
  collectedPatients: 74,
  throughLink: '$7,640',
  atDeskOrPhone: '$3,600',
  throughLinkPct: 68,
  medianDaysToPay: 6,
  withInsurer: '$18,400',
  withInsurerPatients: 61,
  overThousand: '$8,900',
  overThousandPatients: 6,
  disputesFound: 3,
};

/* ---- the follow-up store (§2.2) ------------------------------------------ */

const SEED: ArFollowUp[] = [
  { id: 'missed-fees', name: 'Missed-appointment fees', description: 'No-show or late-cancellation fee · one text, nothing else', state: 'active', inProgress: 8, stepCount: 1, channels: 'text', paid30: 5, value30: '$660' },
  { id: 'new-balances', name: 'New balances', description: 'Became theirs 7 to 30 days ago', state: 'active', inProgress: 22, stepCount: 2, channels: 'text · email', paid30: 38, value30: '$4,980' },
  { id: 'older-balances', name: 'Older balances', description: '31 to 90 days', state: 'active', inProgress: 42, stepCount: 5, channels: 'text · email · AI call · person', paid30: 24, value30: '$3,860' },
  { id: 'over-90', name: 'Over 90 days', description: '91 days or more', state: 'active', inProgress: 10, stepCount: 2, channels: 'text · person', paid30: 7, value30: '$1,740' },
  { id: 'self-pay-300', name: 'Self-pay over $300', description: 'No insurance, balance $300 or more', state: 'draft', inProgress: 0, stepCount: 0, channels: '', paid30: 0, value30: '$0' },
];

export const arFollowUpStore: { followUps: ArFollowUp[] } = { followUps: SEED.map((f) => ({ ...f })) };

export function arFollowUpById(id: string): ArFollowUp | undefined {
  return arFollowUpStore.followUps.find((f) => f.id === id);
}

export function reorderArFollowUps(from: number, to: number): void {
  const list = [...arFollowUpStore.followUps];
  const moved = list[from];
  if (moved === undefined) return;
  list.splice(from, 1);
  list.splice(to, 0, moved);
  arFollowUpStore.followUps = list;
}

export function setArFollowUpState(id: string, state: ArFollowUp['state']): void {
  arFollowUpStore.followUps = arFollowUpStore.followUps.map((f) => (f.id === id ? { ...f, state } : f));
}

export function duplicateArFollowUp(id: string): void {
  const row = arFollowUpById(id);
  if (row === undefined) return;
  arFollowUpStore.followUps = [
    ...arFollowUpStore.followUps,
    { ...row, id: `${row.id}-copy-${String(Date.now())}`, name: `${row.name} (copy)`, state: 'draft', inProgress: 0, paid30: 0, value30: '$0' },
  ];
}

export function deleteArFollowUp(id: string): void {
  arFollowUpStore.followUps = arFollowUpStore.followUps.filter((f) => f.id !== id);
}

/** The Follow-ups tab eyebrow — computed, so it always agrees with the rows. */
export function arFollowUpsHeader(): string {
  const active = arFollowUpStore.followUps.filter((f) => f.state === 'active').length;
  return `${String(active)} follow-ups active · ${String(AR_NUMBERS.inFollowUp)} patients · ${AR_NUMBERS.collected30} collected in the last 30 days`;
}

/* ---- hold (§3, spec 24 §6) ----------------------------------------------- */

export const arHoldStore = { get onHold(): boolean { return getAgentHold('ar').held; } };
export function setArHold(onHold: boolean): void {
  if (onHold) holdAgent('ar', { by: 'Ava Thompson', since: '9:10 am', waiting: 14 });
  else resumeAgent('ar');
}

/* ---- §2.1 Balances: the subtraction funnel ------------------------------- */

export const AR_FUNNEL: ArFunnelRow[] = [
  { key: 'outstanding', label: 'Outstanding patient balances', patients: 212, value: '$47,900', kind: 'gross' },
  { key: 'insurer', label: 'The insurer is still deciding', note: 'An open claim or a denial that can be appealed. Not asked of the patient', patients: 61, value: '−$18,400', kind: 'deduction', seeWho: true },
  { key: 'plan', label: 'On a payment plan, and up to date', patients: 14, value: '−$6,100', kind: 'deduction' },
  { key: 'disputed', label: 'Disputed, opted out, or at an agency', note: 'A person clears these', patients: 7, value: '−$2,300', kind: 'deduction' },
  { key: 'no-contact', label: 'No mobile or email', patients: 3, value: '−$410', kind: 'deduction' },
  { key: 'under-25', label: 'Under $25', patients: 24, value: '−$380', kind: 'deduction' },
  { key: 'followable', label: 'Can be followed up', patients: 103, value: '$20,310', kind: 'total' },
];

export const AR_FUNNEL_NOTE = {
  waitingLead: '21 of the 103 are waiting',
  waitingBody: ' — 9 are coming in within 14 days (the desk collects at the visit), 12 became theirs less than 7 days ago. ',
  nowLead: '82 can be contacted now',
  nowBody: ', all of them in a follow-up.',
};

export const AR_SIDE_TILES: ArSideTile[] = [
  { eyebrow: 'Collected · last 30 days', value: '$11,240', sub: '74 patients · $7,640 through the payment link' },
  { eyebrow: 'With the insurer', value: '$18,400', sub: '61 patients · ELVA doesn’t ask them', seeWho: true },
  { eyebrow: 'Over $1,000', value: '$8,900', sub: '6 patients · a person makes the call, not ELVA' },
];

export const AR_BALANCE_BREAKDOWNS: ArBreakdown[] = [
  {
    n: '01', title: 'By how old it is', sub: 'Days since the balance became the patient’s', colLabel: 'Age',
    rows: [
      { key: '0-30', label: '0 – 30 days', patients: 38, value: '$6,900', tag: 'In New balances', followUpDescription: 'Balances that became theirs in the last 30 days' },
      { key: '31-60', label: '31 – 60 days', patients: 29, value: '$5,800', tag: 'In Older balances', followUpDescription: 'Balances between 31 and 60 days old' },
      { key: '61-90', label: '61 – 90 days', patients: 21, value: '$4,300', tag: 'In Older balances', followUpDescription: 'Balances between 61 and 90 days old' },
      { key: 'over-90', label: 'Over 90 days', patients: 15, value: '$3,310', tag: 'In Over 90 days', followUpDescription: 'Balances more than 90 days old' },
    ],
  },
  {
    n: '02', title: 'By size', colLabel: 'Balance',
    rows: [
      { key: 'under-100', label: 'Under $100', patients: 50, value: '$2,050', followUpDescription: 'Balances under $100' },
      { key: '100-300', label: '$100 – $300', patients: 38, value: '$5,760', followUpDescription: 'Balances between $100 and $300' },
      { key: '300-1000', label: '$300 – $1,000', patients: 9, value: '$3,600', followUpDescription: 'Balances between $300 and $1,000' },
      { key: 'over-1000', label: 'Over $1,000', patients: 6, value: '$8,900', tag: 'A person calls', followUpDescription: 'Balances over $1,000' },
    ],
  },
  {
    n: '03', title: 'What it’s for', colLabel: 'For',
    rows: [
      { key: 'treatment', label: 'Treatment', patients: 84, value: '$19,030', followUpDescription: 'Balances for treatment' },
      { key: 'missed-fees', label: 'Missed-appointment fees', patients: 11, value: '$770', followUpDescription: 'No-show or late-cancellation fees' },
      { key: 'other', label: 'Other', patients: 8, value: '$510', followUpDescription: 'Balances for anything else' },
    ],
  },
];

/** The 61 patients behind the insurer row (§2.1, §3 See-who). A readable sample. */
export const AR_INSURER_LIST: ArInsurerPatient[] = [
  { id: 'i1', name: 'Gloria Adeyemi', balance: '$480', claim: 'waiting on Delta since 12 Sep' },
  { id: 'i2', name: 'Marcus Feld', balance: '$1,240', claim: 'denial being appealed · Cigna, 8 Sep' },
  { id: 'i3', name: 'Priya Anand', balance: '$320', claim: 'waiting on MetLife since 19 Sep' },
  { id: 'i4', name: 'Theo Marchetti', balance: '$96', claim: 'waiting on Aetna since 21 Sep' },
  { id: 'i5', name: 'Sandra Beaulieu', balance: '$184', claim: 'resubmitted to Delta, 15 Sep' },
  { id: 'i6', name: 'Devin Okafor', balance: '$512', claim: 'waiting on Guardian since 9 Sep' },
  { id: 'i7', name: 'Harold Pinkett', balance: '$940', claim: 'denial being appealed · United, 3 Sep' },
  { id: 'i8', name: 'Bianca Sol', balance: '$410', claim: 'waiting on Delta since 17 Sep' },
];

/* ---- §3 agent page ------------------------------------------------------- */

export const AR_TODAY: ArTodayStat[] = [
  { value: '14', label: 'messages sent' },
  { value: '3', label: 'AI calls' },
  { value: '5', label: 'paid' },
  { value: '$860', label: 'collected' },
];

/** By-follow-up table (card 01) — derived so it agrees with the store. */
export function arByFollowUp(): ArByFollowUpRow[] {
  const map: Record<string, { paid: number; collected: string }> = {
    'missed-fees': { paid: 5, collected: '$660' },
    'new-balances': { paid: 38, collected: '$4,980' },
    'older-balances': { paid: 24, collected: '$3,860' },
    'over-90': { paid: 7, collected: '$1,740' },
  };
  return arFollowUpStore.followUps
    .filter((f) => f.state === 'active')
    .map((f) => ({ name: f.name, inProgress: f.inProgress, paid: map[f.id]?.paid ?? f.paid30, collected: map[f.id]?.collected ?? f.value30 }));
}

export const AR_PAID_AFTER: ArPaidAfterRow[] = [
  { label: 'A text', patients: 38 },
  { label: 'An email', patients: 14 },
  { label: 'An AI call', patients: 12 },
  { label: 'A call by a person', patients: 5 },
  { label: 'The one missed-fee text', patients: 5 },
];

export const AR_LATEST: ArLatestItem[] = [
  { body: 'Renee Castillo paid $146 on the payment link — the morning after the email', at: '22 min ago' },
  { body: 'ELVA called Sandra Beaulieu — explained the $184, texted the link, paid during the call', at: '3h ago' },
  { body: 'Joel Aranda replied that the $95 fee is wrong — follow-up stopped, task for the billing coordinator', at: '4h ago' },
  { body: 'Maya Brooks is waiting — she’s in on Thursday, the desk will collect', at: '5h ago' },
  { body: 'Aaron Vasquez owes $1,340 — a person makes his call, in Calls', at: 'Yesterday' },
];

export const arTriageBanner = {
  lead: '3 need a person',
  detail: '2 replied about their bill · 1 asked about a payment plan',
};

/* ---- §4 the builder: fields + steps + audience --------------------------- */

export const AR_FIELDS: MsgField<ArFieldKey>[] = [
  { key: 'first_name', label: 'First name', example: 'Curtis', mayBeEmpty: true, defaultFallback: 'there' },
  { key: 'balance', label: 'Balance', example: '$212', mayBeEmpty: false },
  { key: 'what_for', label: 'What it’s for', example: 'a crown', mayBeEmpty: false },
  { key: 'insurance_paid', label: 'Insurance paid', example: '$848', mayBeEmpty: true, defaultFallback: 'your insurer' },
  { key: 'payment_link', label: 'Payment link', example: 'their secure link', mayBeEmpty: false },
  { key: 'practice_name', label: 'Practice name', example: 'Alamo Heights Dental', mayBeEmpty: false },
  { key: 'practice_phone', label: 'Practice phone', example: '(210) 555-0147', mayBeEmpty: false },
  { key: 'plan_line', label: 'Plan line', example: 'a plan is possible', mayBeEmpty: false },
];

const AR_KEYS: ReadonlySet<string> = new Set(AR_FIELDS.map((f) => f.key));
const t = (...parts: string[]): MsgToken<ArFieldKey>[] => tk(AR_KEYS, ...parts);

/**
 * The starting steps for a fresh follow-up (§4.2). A short, sane text-then-email
 * default; a new follow-up always leads with a text carrying the payment link.
 */
export function arStartingSteps(): ArStep[] {
  return [
    {
      id: 's1', channel: 'text', day: 0, mode: 'template', whoGets: 'everyone',
      body: t('Hi ', 'first_name', ', your balance of ', 'balance', ' is for ', 'what_for', '. You can pay securely here: ', 'payment_link'),
    },
    {
      id: 's2', channel: 'email', day: 7, mode: 'smart', whoGets: 'everyone',
      subject: t('Your ', 'balance', ' balance'),
      body: t('What the ', 'balance', ' is for, and how to pay it: ', 'payment_link'),
    },
  ];
}

/** A blank draft for /collections/follow-ups/new. */
export function blankArDraft(description = ''): ArFollowUpDraft {
  return { id: `ar-${String(Date.now())}`, name: '', description, steps: arStartingSteps() };
}

/**
 * The Older-balances follow-up, fully built (§4.2, the messages mockup). Five
 * steps: text d0 · email d5 in two versions · AI call d12 · text d20 to
 * didn't-open · human call d30.
 */
export function olderBalancesSteps(): ArStep[] {
  return [
    {
      id: 'os1', channel: 'text', day: 0, mode: 'template', whoGets: 'everyone',
      body: t('Hi ', 'first_name', ', your balance of ', 'balance', ' is for ', 'what_for', '. ', 'insurance_paid', ' was covered by insurance. Pay securely: ', 'payment_link'),
    },
    {
      id: 'os2', channel: 'email', day: 5, mode: 'smart', whoGets: 'everyone', twoVersions: true,
      subject: t('What your ', 'balance', ' is for'),
      body: t('Here’s what the ', 'balance', ' is for, line by line. ', 'insurance_paid', ' was paid by insurance. Pay here: ', 'payment_link'),
      subjectOpened: t('Your ', 'balance', ' — a quick breakdown'),
      bodyOpened: t('You looked at the ', 'balance', ' — here’s exactly what it covers. ', 'insurance_paid', ' was insurance. Pay here: ', 'payment_link'),
      cardNote: 'What the balance is for, line by line',
    },
    {
      id: 'os3', channel: 'ai_call', day: 12, mode: 'smart', whoGets: 'everyone',
      brief: 'Offer to explain the bill first. Say what the visit was, what insurance paid and what’s left. If they want to pay, text them the link while you’re on the call. If they say it’s wrong, thank them and say someone from the practice will look into it.',
      cardNote: 'Explain the bill, offer to text the link',
    },
    {
      id: 'os4', channel: 'text', day: 20, mode: 'template', whoGets: 'not_opened',
      body: t('Hi ', 'first_name', ', a quick reminder about your ', 'balance', ' balance. Pay securely here: ', 'payment_link'),
      cardNote: 'Hi {First name}, a quick reminder…',
    },
    {
      id: 'os5', channel: 'human_call', day: 30, mode: 'smart', whoGets: 'everyone',
      brief: 'A call task in Triage, with everything tried.',
      cardNote: 'A call task in Triage, with everything tried',
    },
  ];
}

/** The audience for the described new follow-up (§4.1). */
export const AR_AUDIENCE: ArAudiencePatient[] = [
  { id: 'a1', name: 'Curtis Nwachukwu', age: 48, insurance: 'Delta Dental', whatFor: 'Crown · 12 Aug', balance: '$212', ageDays: '61 days', linkStatus: 'Not opened', status: 'will-get-it', statusLabel: 'Will get it' },
  { id: 'a2', name: 'Isabel Moreno', age: 35, insurance: 'Cigna', whatFor: 'Deep cleaning · 29 Jul', balance: '$318', ageDays: '63 days', linkStatus: 'Opened, not paid', status: 'will-get-it', statusLabel: 'Will get it' },
  { id: 'a3', name: 'Nadia Farouk', age: 57, insurance: 'MetLife', whatFor: 'Root canal · 14 Jul', balance: '$640', ageDays: '78 days', linkStatus: '—', status: 'will-get-it', statusLabel: 'Will get it' },
  { id: 'a4', name: 'Carlos Mendez', age: 29, insurance: 'Aetna', whatFor: 'Filling · 2 Jul', balance: '$146', ageDays: '90 days', linkStatus: 'Opened, not paid', status: 'waiting', statusLabel: 'Waiting · in on 6 Oct' },
  { id: 'a5', name: 'Victor Lang', age: 41, insurance: 'Delta Dental', whatFor: 'Crown · 20 Jun', balance: '$905', ageDays: '102 days', linkStatus: 'Not opened', status: 'will-get-it', statusLabel: 'Will get it' },
];

/** The readback the audience box shows (§4.1). */
export const AR_READBACK: {
  understood: ArReadbackItem[];
  leftOut: { phrase: string; reason: string };
  alwaysApplied: string[];
  match: number;
  matchValue: string;
  whereFirst: string;
  whereSecond: string;
} = {
  understood: [
    { label: 'Balance', value: 'between $100 and $1,000' },
    { label: 'Balance age', value: 'more than 60 days' },
    { label: 'Insurance', value: 'has insurance' },
  ],
  leftOut: {
    phrase: 'who are good payers',
    reason: 'Follow-ups choose balances — how much, how old, what for — never a person’s means or history as a payer.',
  },
  alwaysApplied: [
    'The insurer has finished with it · no open claim, no denial that can be appealed',
    'Not on a payment plan that’s up to date',
    'Not disputed, opted out, or at an agency',
    'Can be contacted · a mobile or email',
    '$25 or more',
    'No appointment in the next 14 days · the desk collects at the visit',
    'The balance has been theirs for 7 days or more',
  ],
  match: 19,
  matchValue: '$5,140',
  whereFirst: '15 in Older balances',
  whereSecond: '4 in Over 90 days',
};

/** Starting-point cards on the audience page (§4.1): the 4 follow-ups + Balances. */
export const AR_STARTING_POINTS: ArStartingPoint[] = [
  { id: 'missed-fees', name: 'Missed-appointment fees', sub: 'No-show or late-cancellation fee', patients: 11, description: 'No-show or late-cancellation fees' },
  { id: 'new-balances', name: 'New balances', sub: '7 to 30 days old', patients: 38, description: 'Balances that became theirs 7 to 30 days ago' },
  { id: 'older-balances', name: 'Older balances', sub: '31 to 90 days', patients: 50, description: 'Balances between 31 and 90 days old' },
  { id: 'over-90', name: 'Over 90 days', sub: '91 days or more', patients: 15, description: 'Balances more than 90 days old' },
  { id: 'over-1000', name: 'Over $1,000', sub: 'From Balances › By size', patients: 6, description: 'Balances over $1,000', fromBalances: true },
];

/* ---- the AI-call step brief boxes + outcomes (§4.2 AR4CallStep) ----------- */

export const AR_CALL_ALWAYS = [
  'Say who’s calling, from which practice',
  'Check it’s the patient — date of birth — before any amount',
  'What it’s for and what insurance paid',
  'Offer to text the payment link',
];

export const AR_CALL_NEVER = [
  'Take card details by voice',
  'Agree a plan, a discount or a later date',
  'The five money rules — threat, collections, credit, legal, holding back treatment',
  'The amount or the reason in a voicemail',
];

export const AR_CALL_NEXT: { label: string; body: string }[] = [
  { label: 'Wants to pay', body: 'ELVA texts the payment link during the call. Paying ends the follow-up' },
  { label: 'Says it’s wrong', body: 'Follow-up stops. A task for a person, and RCM is told' },
  { label: 'Asks about a plan, or for more time', body: 'Follow-up pauses. A call task for a person' },
  { label: 'No answer', body: 'A short voicemail. The next step carries on' },
];

/* ---- §2.3 Payment links: staff + follow-up links, together --------------- */

/** The true totals behind the filter chips (§2.3): they add up. */
export const AR_LINK_COUNTS = { all: 94, waiting: 21, paid: 58, expired: 13, cancelled: 2 };

export const AR_LINK_EYEBROW = '21 links waiting · $6,480 not yet paid · $10,700 collected through links in 30 days';

export const AR_LINK_FOOTER = 'Links from your team and from follow-ups, together. $10,700 = $7,640 from follow-up links + $3,060 from links your team sent.';

/** The follow-up names for the Sent-by filter (§2.3). */
export const AR_LINK_FOLLOWUPS = ['Missed-appointment fees', 'New balances', 'Older balances', 'Over 90 days'];

/**
 * A representative set of the 94 links — team-sent and follow-up-sent, with the
 * new What-happened states (posted to Ascend, paid during the call, disputed by
 * reply). The chips show the true totals from AR_LINK_COUNTS, not row counts,
 * exactly as "the first N" elsewhere.
 */
export const AR_LINK_ROWS: ArPaymentLinkRow[] = [
  // waiting
  { id: 'al1', patient: 'Curtis Nwachukwu', patientId: 'p-3182', label: 'Crown balance', amount: 212, channel: 'text', destination: '(210) 555‑0148', destinationKind: 'phone', sentRelative: '2 hours ago', sentAbsolute: 'Sep 30, 11:12 am', status: 'waiting', outcomeLead: 'Opened, not paid', outcomeSub: 'Opened twice on a phone.', payUrl: 'https://elva.to/p/7Kq2', canPayNow: true, sentBy: { kind: 'follow-up', label: 'Older balances' } },
  { id: 'al2', patient: 'Isabel Moreno', patientId: 'p-2044', amount: 318, channel: 'email', destination: 'i.moreno@gmail.com', destinationKind: 'email', sentRelative: 'Yesterday, 5:12 pm', sentAbsolute: 'Sep 29, 5:12 pm', status: 'waiting', outcomeLead: 'Delivered', outcomeSub: 'Sent from the Older balances follow-up.', payUrl: 'https://elva.to/p/3Btx', canPayNow: true, sentBy: { kind: 'follow-up', label: 'Older balances' } },
  { id: 'al3', patient: 'Grace Winslow', patientId: 'p-1290', label: 'Copay', amount: 90, channel: 'text', destination: '(210) 555‑0172', destinationKind: 'phone', sentRelative: 'Yesterday, 1:04 pm', sentAbsolute: 'Sep 29, 1:04 pm', status: 'waiting', outcomeLead: 'Delivered, not opened', outcomeSub: 'Ana at the front desk sent this.', payUrl: 'https://elva.to/p/9Wmz', canPayNow: true, sentBy: { kind: 'team', label: 'Ana Ruiz' } },
  // paid
  { id: 'al4', patient: 'Renee Castillo', patientId: 'p-4471', amount: 146, channel: 'email', destination: 'renee.c@outlook.com', destinationKind: 'email', sentRelative: 'Yesterday', sentAbsolute: 'Sep 29, 8:40 am', status: 'paid', outcomeLead: 'Paid $146', outcomeSub: 'The morning after the email · posted to Ascend.', sentBy: { kind: 'follow-up', label: 'New balances' } },
  { id: 'al5', patient: 'Sandra Beaulieu', patientId: 'p-3355', label: 'Balance after insurance', amount: 184, channel: 'text', destination: '(210) 555‑2231', destinationKind: 'phone', sentRelative: '3 hours ago', sentAbsolute: 'Sep 30, 9:58 am', status: 'paid', outcomeLead: 'Paid during ELVA’s call', outcomeSub: 'Link texted mid-call · posted to Ascend.', sentBy: { kind: 'follow-up', label: 'Older balances' } },
  { id: 'al6', patient: 'Devin Okafor', patientId: 'p-6620', amount: 512, channel: 'text', destination: '(408) 555‑0119', destinationKind: 'phone', sentRelative: 'Sep 28', sentAbsolute: 'Sep 28, 4:44 pm', status: 'paid', outcomeLead: 'Paid $512', outcomeSub: 'Card · 1h 26m after sending · posted to Ascend.', sentBy: { kind: 'team', label: 'Marcus Feld' } },
  { id: 'al7', patient: 'Lena Hoffmann', patientId: 'p-7781', label: 'Whitening', amount: 275, channel: 'email', destination: 'lena.h@icloud.com', destinationKind: 'email', sentRelative: 'Sep 27', sentAbsolute: 'Sep 27, 10:15 am', status: 'paid', outcomeLead: 'Paid $275', outcomeSub: 'Next morning · posted to Ascend.', sentBy: { kind: 'team', label: 'Ana Ruiz' } },
  // expired
  { id: 'al8', patient: 'Harold Pinkett', patientId: 'p-9040', label: 'Bridge balance', amount: 940, channel: 'email', destination: 'hpinkett@aol.com', destinationKind: 'email', sentRelative: 'Sep 24', sentAbsolute: 'Sep 24, 9:00 am', status: 'expired', outcomeLead: 'Never opened', outcomeExpiry: 'expired Sep 27', outcomeSub: 'Email may be wrong — the follow-up moved to a text.', sentBy: { kind: 'follow-up', label: 'Over 90 days' } },
  // cancelled / disputed
  { id: 'al9', patient: 'Joel Aranda', patientId: 'p-2277', label: 'Missed-appointment fee', amount: 95, channel: 'text', destination: '(650) 555‑0188', destinationKind: 'phone', sentRelative: '4 hours ago', sentAbsolute: 'Sep 30, 8:30 am', status: 'canceled', outcomeLead: 'Disputed by reply', outcomeSub: 'Follow-up stopped, sent to a person.', sentBy: { kind: 'follow-up', label: 'Missed-appointment fees' } },
];
